<script setup>
import { ref, computed, nextTick, onMounted } from 'vue'
import { DxDataGrid } from 'devextreme-vue/data-grid'
import { getI18n } from '@/plugins/i18n'

import { usePlanningStore } from '@/stores/planning.store'
import { useScheduleStore } from '@/stores/schedule.store'
import { useWebSiteStore } from '@/stores/web-site.store'
import { useGridRef, useDefaultGridOptions, useLookup, refreshGridAsync } from '@/composables/data-grid'
import * as dataGridColumn from '@/composables/data-grid-column'
import { countTaskProgress, useMyTaskFilter, useTaskActions, useTaskLoader, useTaskMeta, useTaskServiceOptions, taskDueLabel, taskDueClass, projectMissingServiceIds } from '@/composables/tasks'

import ConfirmDialog from '@/components/shared/ConfirmDialog.vue'
import MissingServicesWarning from '@/components/task/MissingServicesWarning.vue'
import ServiceLinkChips from '@/components/shared/ServiceLinkChips.vue'
import TaskCheckButton from '@/components/task/TaskCheckButton.vue'
import TaskEdit from '@/components/task/TaskEdit.vue'
import TaskTypeChip from '@/components/task/TaskTypeChip.vue'

const props = defineProps({
  projectId: {
    type: Number,
    required: true,
  },
})

const i18n = getI18n()
const planningStore = usePlanningStore()
const scheduleStore = useScheduleStore()
const webSiteStore = useWebSiteStore()
const { gridRef } = useGridRef()
const { ensureServicesAsync } = useTaskServiceOptions()
const { extraValueChips } = useTaskMeta()

const editTask = ref(null)
const taskToDelete = ref(null)
const showDeleteDialog = ref(false)

const highlightTaskAsync = async(taskId) => {
  await nextTick()
  await refreshGridAsync(gridRef, taskId)
}

const { tasks, loadAsync } = useTaskLoader(() => scheduleStore.fetchTasksAsync({ project: props.projectId }), { onNewTaskAsync: highlightTaskAsync })

const isMine = useMyTaskFilter()
const visibleTasks = computed(() => (webSiteStore.projectTasksShowMine ? tasks.value.filter(isMine) : tasks.value))
const tasksOfType = (taskTypeId, source = tasks.value) => source.filter(task => (task.task_type ?? null) === (taskTypeId ?? null))
const typeOrder = (taskTypeId) => (taskTypeId == null ? -1 : scheduleStore.getTaskTypeById(taskTypeId)?.order ?? 0)
const typeProgressLabel = (taskTypeId) => {
  const { done, total } = countTaskProgress(tasksOfType(taskTypeId, visibleTasks.value))
  return `${done}/${total}`
}
const positions = computed(() => {
  const counters = new Map()
  return Object.fromEntries(tasks.value.map((task) => {
    const key = task.task_type ?? null
    counters.set(key, (counters.get(key) ?? 0) + 1)
    return [task.id, counters.get(key)]
  }))
})
const missingServiceIds = computed(() => projectMissingServiceIds(tasks.value.filter(task => !task.completed)))
const hasExtraValues = computed(() => tasks.value.some(task => extraValueChips(task).length))

const { toggleCompletedAsync, deleteAsync: deleteTaskAsync, newTaskDefaults } = useTaskActions()

onMounted(() => Promise.all([scheduleStore.ensureTaskTypesAsync(), ensureServicesAsync(), loadAsync()]))

const columns = computed(() => [
  {
    type: 'buttons',
    buttons: [
      { hint: i18n.t('common.edit'), icon: 'edit', visible: (e) => e.row.data.can_edit, onClick: (e) => { editTask.value = e.row.data } },
      { hint: i18n.t('common.delete'), icon: 'trash', visible: (e) => e.row.data.can_edit, onClick: (e) => { taskToDelete.value = e.row.data; showDeleteDialog.value = true } },
    ],
    fixed: false,
    visibleIndex: -1,
    showInColumnChooser: false,
    width: '5rem',
  },
  {
    dataField: 'completed',
    caption: i18n.t('common.tasks.isDone'),
    dataType: 'boolean',
    width: '5rem',
    cellTemplate: 'completed-cell-template',
  },
  {
    caption: '#',
    width: '3.5rem',
    allowFiltering: false,
    calculateCellValue: (row) => positions.value[row.id],
    cellTemplate: 'number-cell-template',
  },
  {
    dataField: 'title',
    caption: i18n.t('common.title'),
    minWidth: '14rem',
  },
  {
    dataField: 'task_type',
    caption: i18n.t('common.type'),
    width: '10rem',
    groupIndex: 0,
    sortingMethod: (a, b) => typeOrder(a) - typeOrder(b),
    lookup: useLookup({ dataSource: () => scheduleStore.getTaskTypes(), valueExpr: 'id', displayExpr: 'name', allowClearing: true }),
    cellTemplate: 'type-cell-template',
    groupCellTemplate: 'type-group-template',
  },
  { ...dataGridColumn.useDescriptionColumn(), minWidth: '12rem', visible: false },
  {
    dataField: 'extra_values',
    caption: i18n.t('common.details'),
    minWidth: '10rem',
    visible: hasExtraValues.value,
    allowFiltering: false,
    calculateCellValue: (row) => extraValueChips(row).map(chip => chip.label).join(', '),
    cellTemplate: 'extra-values-cell-template',
  },
  {
    dataField: 'services',
    caption: i18n.t('common.tasks.resources'),
    minWidth: '14rem',
    allowFiltering: false,
    calculateCellValue: (row) => (row.services ?? []).map(id => planningStore.getServiceName(planningStore.getServiceById(id))).join(', '),
    cellTemplate: 'services-cell-template',
  },
  dataGridColumn.useDateColumn({
    dataField: 'due_date',
    caption: i18n.t('common.tasks.dueDate'),
    cellTemplate: 'due-cell-template',
  }),
  {
    dataField: 'assignees',
    caption: i18n.t('common.tasks.assignee'),
    minWidth: '12rem',
    allowFiltering: false,
    calculateCellValue: (row) => (row.assignees ?? []).map(id => planningStore.getServiceName(planningStore.getStaffById(id))).join(', '),
    cellTemplate: 'assignees-cell-template',
  },
])

const gridOptions = useDefaultGridOptions({
  isDetailGrid: true,
  keyExpr: 'id',
  remoteOperations: false,
  columnWidth: 'auto',
  sorting: { mode: 'none' },
  grouping: { autoExpandAll: true, allowCollapsing: true },
  groupPanel: { visible: true },
  viewToolbarItems: [{ location: 'after', template: 'mine-toggle' }],
  editing: { allowAdding: false, allowUpdating: false, allowDeleting: false },
  rowDragging: {
    allowReordering: true,
    onDragStart: (e) => { e.cancel = !e.itemData.can_edit },
    onReorder: (e) => onReorderAsync(e),
  },
  addButton: {
    onClick: () => { editTask.value = newTaskDefaults(props.projectId) },
  },
})

const onDeleteConfirmAsync = async() => {
  await deleteTaskAsync(taskToDelete.value.id)
  taskToDelete.value = null
}

const onReorderAsync = async(e) => {
  const target = e.component.getVisibleRows()[e.toIndex]
  if (target?.rowType !== 'data' || target.key === e.itemData.id || (target.data.task_type ?? null) !== (e.itemData.task_type ?? null)) return
  const taskIds = tasksOfType(e.itemData.task_type).map(task => task.id).filter(id => id !== e.itemData.id)
  taskIds.splice(taskIds.indexOf(target.key) + (e.fromIndex < e.toIndex ? 1 : 0), 0, e.itemData.id)
  await scheduleStore.reorderTasksAsync(taskIds)
}
</script>

<template>
  <div>
    <confirm-dialog
      v-model="showDeleteDialog"
      :message="i18n.t('common.confirmDelete')"
      icon="mdi-delete-outline"
      icon-color="negative"
      @confirm="onDeleteConfirmAsync"
    />
    <missing-services-warning class="q-mb-xs" :service-ids="missingServiceIds" />
    <dx-data-grid
      ref="gridRef"
      v-bind="gridOptions"
      :columns="columns"
      :data-source="visibleTasks"
    >
      <template #number-cell-template="{ data: cell }">
        <span class="text-grey-7">{{ cell.value }}</span>
      </template>
      <template #completed-cell-template="{ data: cell }">
        <task-check-button :task="cell.data" @toggle="toggleCompletedAsync" />
      </template>
      <template #mine-toggle>
        <q-toggle
          v-model="webSiteStore.projectTasksShowMine"
          :label="i18n.t('common.tasks.showMine')"
          color="primary"
        />
      </template>
      <template #type-group-template="{ data: group }">
        <div class="row items-center no-wrap q-gutter-x-sm">
          <task-type-chip :task-type="scheduleStore.getTaskTypeById(group.value)" />
          <span class="text-grey-7">{{ typeProgressLabel(group.value) }}</span>
        </div>
      </template>
      <template #type-cell-template="{ data: cell }">
        <task-type-chip :task-type="scheduleStore.getTaskTypeById(cell.data.task_type)" />
      </template>
      <template #extra-values-cell-template="{ data: cell }">
        <div class="row q-gutter-xs">
          <q-chip
            v-for="chip in extraValueChips(cell.data)"
            :key="chip.key"
            dense
            size="sm"
            class="q-ml-none"
            :label="chip.label"
          >
            <q-tooltip>{{ chip.tooltip }}</q-tooltip>
          </q-chip>
        </div>
      </template>
      <template #services-cell-template="{ data: cell }">
        <service-link-chips
          :service-ids="cell.data.services ?? []"
          :warning-ids="cell.data.missing_service_ids ?? []"
          :warning-tooltip="i18n.t('common.tasks.notInProject')"
        />
      </template>
      <template #assignees-cell-template="{ data: cell }">
        <service-link-chips :service-ids="cell.data.assignees ?? []" />
      </template>
      <template #due-cell-template="{ data: cell }">
        <span :class="taskDueClass(cell.data)">{{ taskDueLabel(cell.data) }}</span>
      </template>
    </dx-data-grid>

    <q-dialog :model-value="editTask !== null" class="dialog-md" @hide="editTask = null">
      <task-edit
        v-if="editTask !== null"
        :model-value="editTask"
        @update="editTask = null"
        @cancel="editTask = null"
      />
    </q-dialog>
  </div>
</template>
